import {
    Box,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Flex,
    Heading,
    HStack,
    IconButton,
    Link,
    Text,
    VStack,
    useDisclosure,
    useToast,
    Icon
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import AppDatePicker from "components/AppDatePicker";
import AppSelect from "components/AppSelect";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useRef, useState } from "react";
import { MdMap } from "react-icons/md";
import { Store } from "store";
import { closeLoader, openLoader, showToast, toastTypes } from "utils";
import { useUserRole } from "hooks/useUserRole";
import { TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  tracking_id: string;
  scan_time: string;
  tax_payer: string;
  ip_address: string;
  longitude: string;
  latitude: string;
  user: string;
  qr_code_id: number;
};

export default function TaxLogManagementSection() {
  const loadingRef = useRef<any>(null);
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { dispatch } = useContext(Store);
  const { hasPermission } = useAuthentication();
  const userRole = useUserRole();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [institution, setInstitution] = useState<string>("");
  const [taxNature, setTaxNature] = useState<string>("");
  const toast = useToast();
  const {
    getTaxLogs,
    generateTaxLogReportCSV,
    getInstitutions,
    getTaxNatures,
    deleteTaxLog,
  } = useApi();
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(),
    new Date(),
  ]);
  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, institution, ""));
  const {
    data: taxNatures,
    isLoading: isTaxNatureLoading,
  } = useCache("taxNatures", () =>
    getTaxNatures(1, "", institution, "")
  );
  const { data: logs, mutate } = useCache("getReports", () =>
    getTaxLogs({
      page: page,
      startDate: selectedDates[0]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      endDate: selectedDates[1]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      institution: institution,
      taxNature: taxNature,
    })
  );
  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(
      getTaxLogs({
        page: page,
        startDate: selectedDates[0]
          ?.toISOString()
          .split("T")[0]
          .split("-")
          .reverse()
          .join("-"),
        endDate: selectedDates[1]
          ?.toISOString()
          .split("T")[0]
          .split("-")
          .reverse()
          .join("-"),
        institution: institution,
        taxNature: taxNature,
      })
    );
  };
  const handleSetNextPage = () => {
    if (logs.length > 0 && logs[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(
        getTaxLogs({
          page: page + 1,
          startDate: selectedDates[0]
            ?.toISOString()
            .split("T")[0]
            .split("-")
            .reverse()
            .join("-"),
          endDate: selectedDates[1]
            ?.toISOString()
            .split("T")[0]
            .split("-")
            .reverse()
            .join("-"),
          institution: institution,
          taxNature: taxNature,
        })
      );
    }
  };
  const handleSetPrevPage = () => {
    if (logs.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(
        getTaxLogs({
          page: page + 1,
          startDate: selectedDates[0]
            ?.toISOString()
            .split("T")[0]
            .split("-")
            .reverse()
            .join("-"),
          endDate: selectedDates[1]
            ?.toISOString()
            .split("T")[0]
            .split("-")
            .reverse()
            .join("-"),
          institution: institution,
          taxNature: taxNature,
        })
      );
    }
  };
  const handleGenerateReportCSV = async () => {
    openLoader(loadingRef);
    const response = await generateTaxLogReportCSV({
      startDate: selectedDates[0]
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      endDate: selectedDates[1]
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
    });
    if (response) {
      dispatch(
        showToast({
          toast: toast,
          message: "Un email avec le lien de téléchargement vous sera envoyé.",
          status: toastTypes.success,
        })
      );
    } else {
      dispatch(
        showToast({
          toast: toast,
          message: "Impossible de générer le rapport.",
          status: toastTypes.error,
        })
      );
    }
    closeLoader(loadingRef);
  };
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("tracking_id", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Identifiant de suivi</Heading>
          </Flex>
        ),
        meta: { width: "200px" },
      }),
      table3ColumnHelper.accessor("tax_payer", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Débiteur</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("user", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Collecteur</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("scan_time", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Date du scan</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("ip_address", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">adresse IP</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"}>
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${info.row.original.latitude},${info.row.original.longitude}`}
              target="_blank"
            >
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<Icon as={MdMap as any} boxSize={4} />}
                borderRadius={"4px"}
              />
            </Link>
            {hasPermission("taxlog", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [hasPermission, isDeleting, activeItem, onConfirmOpen]);

  return (
    <>
      {/* dealer management section */}
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="flex-start"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Scans
            </Heading>
            <></>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
              gap={2}
            >
              <AppDatePicker
                selectedDates={selectedDates}
                onDateChange={(date) => {
                  setSelectedDates(date);
                  mutate(
                    getTaxLogs({
                      startDate: date[0]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      endDate: date[1]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      institutionId: institution,
                      taxNature: taxNature,
                    })
                  );
                }}
              />
              <Flex width={"60%"} direction={"row"} gap={2}>
              {!isInstitutionLoading &&
                userRole === TAX_SUPER_ADMIN && (
                  <AppSelect
                    height={"40px"}
                    id="institution"
                    name="institution"
                    onChange={(field: string, value: any) => {
                      setInstitution(value);
                      mutateInstitution(
                        getInstitutions(1, +(institution || 0), value)
                      );
                      mutate(
                        getTaxLogs({
                          page: page,
                          startDate: selectedDates[0]
                            ?.toISOString()
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-"),
                          endDate: selectedDates[1]
                            ?.toISOString()
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-"),
                          institution: value,
                          taxNature: taxNature,
                        })
                      );
                    }}
                    value={institution as string}
                    placeholder="Veuillez sélectionner une institution"
                    options={institutions}
                    isMulti={false}
                    isLoading={isInstitutionLoading}
                  />
                )}
              {!isTaxNatureLoading && (
                <AppSelect
                  height={"40px"}
                  id="institution"
                  name="institution"
                  onChange={(field: string, value: any) => {
                    setTaxNature(value);
                    mutate(
                      getTaxLogs({
                        page: page,
                        startDate: selectedDates[0]
                          ?.toISOString()
                          .split("T")[0]
                          .split("-")
                          .reverse()
                          .join("-"),
                        endDate: selectedDates[1]
                          ?.toISOString()
                          .split("T")[0]
                          .split("-")
                          .reverse()
                          .join("-"),
                        institution: institution,
                        taxNature: value,
                      })
                    );
                  }}
                  value={taxNature as string}
                  placeholder="Veuillez sélectionner nature de règlement"
                  options={taxNatures}
                  isMulti={false}
                  isLoading={isTaxNatureLoading}
                />
              )}
              </Flex>
            </Flex>
            <Button
              size="2xl"
              colorScheme="red_origin"
              fontWeight={700}
              boxShadow="md"
              alignSelf="stretch"
              borderRadius="8px"
              type="submit"
              onClick={handleGenerateReportCSV}
            >
              Générer un rapport
            </Button>
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={logs}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={logs && logs.length > 0 && logs[0]?.num_of_pages}
          />
        </Box>
      </Flex>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer le log « {activeItem?.tracking_id} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteTaxLog(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Log supprimé avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  mutate(getTaxLogs({
                    page: page,
                    startDate: selectedDates[0]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    endDate: selectedDates[1]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    institution: institution,
                    taxNature: taxNature,
                  }));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
